import React, { useRef, useState } from 'react';
import Modal from '@hero/ui-kit/components/Modal';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import { MarkConfirmActions } from './MarkControls';
import useFocusTrap from '../hooks/useFocusTrap';
import { useMarkThemeStyles } from './MarkLayout';
import * as layout from './markLayout.module.css';

type Props = Omit<React.ComponentProps<typeof Modal>, 'externalControls' | 'onClose'> & {
    onClose: () => void;
    isPending?: boolean;
    dirty?: boolean;
    'aria-label'?: string;
    'aria-labelledby'?: string;
};

const MarkModal: React.FC<Props> = ({
    children,
    onClose,
    isPending = false,
    dirty = false,
    isCancelable = true,
    styles,
    ...props
}) => {
    const themeStyles = useMarkThemeStyles();
    const dialogRef = useRef<HTMLElement | null>(null);
    const [confirmClose, setConfirmClose] = useState(false);
    const requestClose = () => {
        if (!isCancelable || isPending) return;
        if (dirty) setConfirmClose(true);
        else onClose();
    };
    useFocusTrap(dialogRef, requestClose);

    return (
        <>
            <Modal
                {...props}
                className={`${layout.modalOverlay} ${props.className ?? ''}`}
                noDefaultPadding
                splashPadding="small"
                // Keep the dialog open if a route close is blocked by unsaved changes.
                externalControls={[true, () => {}]}
                isCancelable={isCancelable && !isPending}
                onClose={requestClose}
                width="page"
                styles={{
                    ...themeStyles,
                    width: 'min(100rem, 94vw)',
                    maxWidth: '100%',
                    padding: 'clamp(1.6rem, 3vw, 3.2rem)',
                    ...styles
                }}
            >
                <div
                    className={`${layout.workspace} ${layout.modalContent}`}
                    ref={(element) => {
                        const dialog = element?.closest<HTMLElement>('[role="dialog"]') ?? null;
                        dialogRef.current = dialog;
                        if (dialog) {
                            if (props['aria-label'])
                                dialog.setAttribute('aria-label', props['aria-label']);
                            if (props['aria-labelledby'])
                                dialog.setAttribute('aria-labelledby', props['aria-labelledby']);
                        }
                    }}
                >
                    {children}
                </div>
            </Modal>
            {confirmClose && (
                <MarkModal
                    aria-label="Discard unsaved changes?"
                    styles={{ width: 'min(64rem, 94vw)' }}
                    onClose={() => setConfirmClose(false)}
                    isPending={isPending}
                    type="warn"
                >
                    <H role="h4">Discard unsaved changes?</H>
                    <P>Your unsaved edits will be lost if you close this form.</P>
                    <MarkConfirmActions
                        confirmLabel="Discard"
                        cancelLabel="Stay"
                        onConfirm={onClose}
                        onCancel={() => setConfirmClose(false)}
                        disabled={isPending}
                    />
                </MarkModal>
            )}
        </>
    );
};

export default MarkModal;
